import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { PADDING, ICON_SIZES } from '@/constants/residents/homeConstants';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { useResponsive } from '@/hooks/common/useResponsive';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

interface EmptyStateProps {
  title: string;
  description: string;
  iconName: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ 
  title, 
  description, 
  iconName 
}) => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const DESKTOP_COLORS = useDesktopColors();
  const { colors } = useThemeColors();
  const styles = useMemo(() => buildStyles(DESKTOP_COLORS, colors), [DESKTOP_COLORS, colors]);
  const SvgIcon = (() => {
    // Map known Ionicons names to our SVG set, extend as needed
    switch (iconName) {
      case 'people-outline':
        return RESIDENTS_SVG_COMPONENTS.CONNECTION; // matches web empty recipients
      case 'mail-outline':
        return RESIDENTS_SVG_COMPONENTS.EMPTY_INBOX; // matches web empty inbox
      case 'help-circle-outline':
        return RESIDENTS_SVG_COMPONENTS.QUESTION_BUBBLE; // FAQ-like icon
      case 'document-outline':
        return RESIDENTS_SVG_COMPONENTS.DOCUMENT; // matches web empty documents
      case 'calendar-outline':
        return RESIDENTS_SVG_COMPONENTS.NO_EVENTS_ICON; // matches web no events icon
      case 'notifications-outline':
      case 'bell-outline':
        return RESIDENTS_SVG_COMPONENTS.BELL; // matches bulletins/notifications empty state
      // Add more mappings if needed. If web uses a specific SVG not in the set, we should import it.
      // e.g. RESIDENTS_SVG_COMPONENTS.EMPTY_INBOX (NOT PRESENT) – please add if you want exact asset.
      default:
        return null;
    }
  })();

  return (
    <View style={[styles.container, isDesktop && styles.containerDesktop]}>
      {/* Main vertical stack - matches iOS stackView */}
      <View style={styles.stackView}>
        {/* Icon - matches iOS imageView */}
        <View style={styles.iconContainer}>
          {SvgIcon ? (
            <SvgIcon width={ICON_SIZES.jumbo} height={ICON_SIZES.jumbo} color={DESKTOP_COLORS.TEXT_TERTIARY}/>
          ) : (
            <Icon
              name={iconName}
              size={ICON_SIZES.jumbo}
              color={colors.text.muted}
              style={styles.icon}
            />
          )}
        </View>
        
        {/* Title - matches iOS titleLabel */}
        <Text style={[styles.titleLabel, isDesktop && styles.titleLabelDesktop]}>{title}</Text>
        
        {/* Description - matches iOS descriptionLabel */}
        <Text style={styles.descriptionLabel}>{description}</Text>
      </View>
    </View>
  );
};

type DesktopColors = ReturnType<typeof useDesktopColors>;
type ThemeColors = ReturnType<typeof useThemeColors>['colors'];

const buildStyles = (DESKTOP_COLORS: DesktopColors, colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg.base, // iOS: .Background.primary
    justifyContent: 'center',
    alignItems: 'center',
  },
  containerDesktop: {
    backgroundColor: 'transparent',
  },
  stackView: {
    alignItems: 'center', // iOS: alignment = .center
    paddingHorizontal: PADDING.padding12, // iOS: .Padding.padding12 (48px)
  },
  iconContainer: {
    marginBottom: PADDING.padding4, // iOS: setCustomSpacing(.Padding.padding4, after: imageView)
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    // iOS: contentMode = .scaleAspectFit and pinAspectRatioTo(1.0)
  },
  titleLabel: {
    fontSize: 14, // iOS: .avenir900(withSize: 14)
    fontWeight: '900', // iOS: avenir900 (black weight)
    color: colors.text.primary, // iOS: .Text.primary
    textAlign: 'center', // iOS: textAlignment = .center
    marginBottom: PADDING.padding1, // Spacing before description
  },
  titleLabelDesktop: {
    fontSize: 16,
    fontWeight: '900',
    color: DESKTOP_COLORS.TEXT_PRIMARY,
  },
  descriptionLabel: {
    fontSize: 13, // iOS: .avenir400(withSize: 13)
    fontWeight: '400', // iOS: avenir400 (regular weight)
    color: colors.text.muted, // iOS: .Text.profileSubtitle
    textAlign: 'center', // iOS: textAlignment = .center
    lineHeight: 18, // Appropriate line height for readability
    // iOS: numberOfLines = 0 (unlimited lines)
  },
});